import { Button, DataTable, type LocalDataColumn, TOUCH } from "@nymbl/nos2-rules";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { TypeChip } from "@/components/time/TypeChip";
import { useMonthTasks } from "@/hooks/use-month";
import { HOME_MONTH_TIME_HELP } from "@/lib/column-help";
import { TIME_ENTRY_TYPE_OPTIONS } from "@/lib/enums";
import { type MonthKey, monthName } from "@/lib/month/month";
import { withCells } from "@/lib/table-columns";
import { formatHours } from "@/lib/time/rules";
import type { MonthTaskRow } from "@/schemas/month";

export const MONTH_TIME_COLUMNS: LocalDataColumn<MonthTaskRow>[] = [
  {
    key: "task",
    label: "Task",
    type: "text",
    accessor: (r) => r.taskName,
    searchText: (r) => r.projectName ?? "",
    help: HOME_MONTH_TIME_HELP.task,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (r) => r.type,
    options: [...TIME_ENTRY_TYPE_OPTIONS],
    help: HOME_MONTH_TIME_HELP.type,
  },
  {
    key: "hours",
    label: "Hours",
    type: "number",
    accessor: (r) => r.hours,
    help: HOME_MONTH_TIME_HELP.hours,
  },
];

const COLUMNS = withCells(MONTH_TIME_COLUMNS, {
  task: {
    render: (r) => (
      <>
        <div className="font-semibold">{r.taskName}</div>
        {r.projectName && <div className="text-xs text-muted-foreground">{r.projectName}</div>}
      </>
    ),
  },
  type: { render: (r) => <TypeChip type={r.type} /> },
  hours: {
    render: (r) => (
      <span className="font-display font-bold tabular-nums">{formatHours(r.hours)}</span>
    ),
  },
});

const byTask = (a: MonthTaskRow, b: MonthTaskRow) =>
  a.taskName.localeCompare(b.taskName) || a.key.localeCompare(b.key);

/** HOME-08: the month's hours by task, PTO included. Every column sorts and filters. */
export function MonthTimePanel({ month }: { month: MonthKey }) {
  const tasks = useMonthTasks(month);
  const rows = useMemo(() => [...(tasks.data ?? [])].sort(byTask), [tasks.data]);
  const title = `${monthName(month)} time`;

  return (
    <DataTable
      label={title}
      title={title}
      search={{ placeholder: "Search tasks…" }}
      exportName="month-time"
      columns={COLUMNS}
      rows={rows}
      rowKey={(r) => r.key}
      defaultSort="hours"
      defaultSortDir="desc"
      loading={tasks.isPending}
      error={tasks.error}
      onRetry={() => void tasks.refetch()}
      empty={{ title: "No time" }}
      controls={
        <Button asChild variant="link" className={TOUCH}>
          <Link to="/dashboard/time">Open Time</Link>
        </Button>
      }
    />
  );
}
